import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_premium-tire-hub-1/artifacts/lhewl2tw_2026-03-07%2021.47.25.jpg";

const leftNavLinks = [
  { name: 'Home', href: '#hero' },
  { name: 'Services', href: '#services' },
  { name: 'Tires', href: '#tires' },
];

const rightNavLinks = [
  { name: 'Reviews', href: '#reviews' },
  { name: 'Contact', href: '#contact' },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('Home');

  const scrollToSection = (href, name) => {
    setMobileMenuOpen(false);
    setActiveLink(name);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className="fixed top-0 w-full z-50 bg-black border-b border-white/10"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 lg:h-32">
          {/* Mobile Menu Button - Left */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white p-2 -ml-2"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Left Navigation - Desktop */}
          <div className="hidden lg:flex items-center space-x-8 flex-1">
            {leftNavLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href, link.name)}
                className={`transition-colors duration-300 font-body text-sm uppercase tracking-wider ${
                  activeLink === link.name ? 'text-[#FF2E2E]' : 'text-white hover:text-[#FF2E2E]'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </button>
            ))}
          </div>

          {/* Center Logo - Large, no border */}
          <a 
            href="#hero" 
            onClick={(e) => { e.preventDefault(); scrollToSection('#hero', 'Home'); }} 
            className="flex items-center justify-center"
          >
            <img 
              src={LOGO_URL} 
              alt="Perry D Tires" 
              className="h-24 lg:h-32 w-auto object-contain"
              data-testid="navbar-logo"
            />
          </a>

          {/* Right Navigation & CTA - Desktop */}
          <div className="hidden lg:flex items-center justify-end space-x-8 flex-1">
            {rightNavLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href, link.name)}
                className={`transition-colors duration-300 font-body text-sm uppercase tracking-wider ${
                  activeLink === link.name ? 'text-[#FF2E2E]' : 'text-white hover:text-[#FF2E2E]'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </button>
            ))}
            <Button 
              onClick={() => scrollToSection('#contact', 'Contact')}
              className="bg-transparent hover:bg-[#FF2E2E] text-white font-heading tracking-wider px-6 py-2 rounded-full border-2 border-[#FF2E2E] transition-all duration-300"
              data-testid="navbar-contact-btn"
            >
              Book Online
            </Button>
          </div>

          {/* Mobile CTA Button - Right */}
          <Button 
            onClick={() => scrollToSection('#contact', 'Contact')}
            className="lg:hidden bg-transparent hover:bg-[#FF2E2E] text-white font-heading text-xs tracking-wider px-4 py-2 rounded-full border border-[#FF2E2E]"
          >
            Book
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black border-t border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-1">
              {[...leftNavLinks, ...rightNavLinks].map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href, link.name)}
                  className={`block w-full text-left py-3 font-body uppercase tracking-wider border-b border-white/5 ${
                    activeLink === link.name ? 'text-[#FF2E2E]' : 'text-white hover:text-[#FF2E2E]'
                  }`}
                >
                  {link.name}
                </button>
              ))}
              <div className="pt-4">
                <Button 
                  onClick={() => scrollToSection('#contact', 'Contact')}
                  className="w-full bg-[#FF2E2E] hover:bg-[#D61E1E] text-white font-heading tracking-wider py-4 rounded-full"
                >
                  Book Online
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
